import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Formik, Form } from 'formik'
import * as y from 'yup'
import { toastr } from 'react-redux-toastr'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Pencil, LoaderCircle } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import SubmittalForm, { SubmittalFormValues } from '../components/SubmittalForm'

const DASH = '—'

const formatDate = (t: (key: string, opts?: any) => string, value?: string | null) => {
  if (!value) return DASH
  return new Date(value).toLocaleDateString(t('date_locale') || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

interface FieldProps {
  label: string
  children: React.ReactNode
}

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <div className="space-y-1">
    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
    <dd className="text-sm mb-0 break-words">{children}</dd>
  </div>
)

interface SubmittalEditDialogProps {
  submittal: any
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refetch: () => void
}

/**
 * Mounted only while the Edit dialog is open (see SubmittalFieldsCard below) so
 * every open gets a fresh Formik instance seeded from the latest submittal data,
 * and the three lookup queries only fire when actually needed — same
 * conditional-mount trick pages/submittals/index.tsx uses for the create dialog.
 */
const SubmittalEditDialog: React.FC<SubmittalEditDialogProps> = ({
  submittal,
  projectId,
  open,
  onOpenChange,
  refetch,
}) => {
  const { t } = useTranslation()

  const { data: lookupsData } = useQuery(Query.SUBMITTAL_LOOKUP_VALUES, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: suggestionsData } = useQuery(Query.SUBMITTAL_COMPANY_SUGGESTIONS, {
    variables: { projectId },
    skip: !projectId,
  })

  const [updateSubmittal] = useMutation(Mutation.UPDATE_SUBMITTAL)

  const lookups = lookupsData?.submittalLookupValues || []
  const members = (membersData?.project?.users ?? []).map((m: any) => m.user).filter(Boolean)
  const companySuggestions: string[] = suggestionsData?.submittalCompanySuggestions || []

  const initialValues: SubmittalFormValues = {
    displayNumber: submittal.displayNumber || '',
    title: submittal.title || '',
    description: submittal.description || '',
    companyName: submittal.companyName || '',
    responsibleId: submittal.responsible?.id || '',
    dueDate: submittal.dueDate || null,
    specSectionId: submittal.specSection?.id || '',
    categoryId: submittal.category?.id || '',
    subcategoryId: submittal.subcategory?.id || '',
    disciplineId: submittal.discipline?.id || '',
    packageId: submittal.package?.id || '',
  }

  const validationSchema = y.object().shape({
    title: y.string().trim().required(t('Submittals_validation_title_required')),
  })

  const handleClose = () => onOpenChange(false)

  const handleSubmit = async (values: SubmittalFormValues, { setSubmitting, setStatus }: any) => {
    setStatus(undefined)
    try {
      await updateSubmittal({
        variables: {
          id: submittal.id,
          input: {
            displayNumber: values.displayNumber?.trim() || undefined,
            title: values.title.trim(),
            description: values.description.trim() || null,
            companyName: values.companyName.trim() || null,
            responsibleId: values.responsibleId || null,
            dueDate: values.dueDate || null,
            specSectionId: values.specSectionId || null,
            categoryId: values.categoryId || null,
            subcategoryId: values.subcategoryId || null,
            disciplineId: values.disciplineId || null,
            packageId: values.packageId || null,
          },
        },
      })
      toastr.success('', t('Submittals_updated'))
      refetch()
      onOpenChange(false)
    } catch (error: any) {
      const message = error.message ? error.message.replace('GraphQL error: ', '') : t('Common_error_occurred')
      setStatus({ submitError: message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            {t('Submittals_edit_title')}
          </DialogTitle>
        </DialogHeader>

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form className="flex-1 overflow-y-auto min-h-0 p-1.5 -m-1.5">
              {/* p-1.5/-m-1.5 above: overflow-y-auto also clips horizontally, so the
                  scroll box needs interior room for the 4px focus ring; the negative
                  margin keeps the fields aligned with the dialog padding. */}
              <SubmittalForm
                lookups={lookups}
                members={members}
                companySuggestions={companySuggestions}
                showDisplayNumber
              />

              <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  {t('Common_cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {isSubmitting ? t('Common_saving') : t('Common_save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

interface SubmittalFieldsCardProps {
  submittal: any
  editable: boolean
  projectId: string
  refetch: () => void
}

const SubmittalFieldsCard: React.FC<SubmittalFieldsCardProps> = ({
  submittal,
  editable,
  projectId,
  refetch,
}) => {
  const { t } = useTranslation()
  const [editOpen, setEditOpen] = useState(false)

  return (
    <Card className="shadow-sm border-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('Submittals_fields_title')}</CardTitle>
        {editable && (
          <Button variant="outline" size="sm" className="h-8" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <Field label={t('Submittals_field_description')}>
            <span className="whitespace-pre-wrap">{submittal.description || DASH}</span>
          </Field>
          <Field label={t('Submittals_field_company')}>{submittal.companyName || DASH}</Field>
          <Field label={t('Submittals_field_responsible')}>{submittal.responsible?.name || DASH}</Field>
          <Field label={t('Submittals_field_due_date')}>{formatDate(t, submittal.dueDate)}</Field>
          <Field label={t('Submittals_field_spec_section')}>{submittal.specSection?.value || DASH}</Field>
          <Field label={t('Submittals_field_category')}>{submittal.category?.value || DASH}</Field>
          <Field label={t('Submittals_field_subcategory')}>{submittal.subcategory?.value || DASH}</Field>
          <Field label={t('Submittals_field_discipline')}>{submittal.discipline?.value || DASH}</Field>
          <Field label={t('Submittals_field_package')}>{submittal.package?.value || DASH}</Field>
          <Field label={t('Submittals_field_created_by')}>
            {submittal.createdBy?.name || DASH} · {formatDate(t, submittal.createdAt)}
          </Field>
        </dl>
      </CardContent>

      {editOpen && (
        <SubmittalEditDialog
          submittal={submittal}
          projectId={projectId}
          open={editOpen}
          onOpenChange={setEditOpen}
          refetch={refetch}
        />
      )}
    </Card>
  )
}

export default SubmittalFieldsCard
